import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AppContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';
import { Plus, Trash2, Package, Layers, ChevronDown, ChevronRight, KeyRound, Zap } from 'lucide-react';

// Common module suggestions per panel type keyword
const MODULE_SUGGESTIONS = {
  default: [
    { key: 'dashboard',  name: 'Dashboard' },
    { key: 'reports',    name: 'Reports' },
    { key: 'settings',   name: 'Settings' },
    { key: 'users',      name: 'Users' },
    { key: 'logs',       name: 'Logs' },
    { key: 'exports',    name: 'Exports' },
  ],
  erp: [
    { key: 'products',   name: 'Products' },
    { key: 'orders',     name: 'Orders' },
    { key: 'inventory',  name: 'Inventory' },
    { key: 'suppliers',  name: 'Suppliers' },
    { key: 'invoices',   name: 'Invoices' },
    { key: 'purchases',  name: 'Purchases' },
    { key: 'warehouse',  name: 'Warehouse' },
    { key: 'shipping',   name: 'Shipping' },
  ],
  crm: [
    { key: 'customers',  name: 'Customers' },
    { key: 'leads',      name: 'Leads' },
    { key: 'deals',      name: 'Deals' },
    { key: 'contacts',   name: 'Contacts' },
    { key: 'campaigns',  name: 'Campaigns' },
    { key: 'tickets',    name: 'Tickets' },
    { key: 'pipeline',   name: 'Pipeline' },
    { key: 'followups',  name: 'Follow-ups' },
  ],
  hr: [
    { key: 'employees',  name: 'Employees' },
    { key: 'payroll',    name: 'Payroll' },
    { key: 'leaves',     name: 'Leaves' },
    { key: 'attendance', name: 'Attendance' },
    { key: 'recruitment',name: 'Recruitment' },
    { key: 'appraisals', name: 'Appraisals' },
    { key: 'training',   name: 'Training' },
    { key: 'documents',  name: 'Documents' },
  ],
  analytics: [
    { key: 'dashboard',  name: 'Dashboard' },
    { key: 'reports',    name: 'Reports' },
    { key: 'exports',    name: 'Exports' },
    { key: 'charts',     name: 'Charts' },
    { key: 'kpis',       name: 'KPIs' },
    { key: 'schedules',  name: 'Schedules' },
  ],
};

function getSuggestions(panelName = '') {
  const n = panelName.toLowerCase();
  if (n.includes('erp'))       return MODULE_SUGGESTIONS.erp;
  if (n.includes('crm'))       return MODULE_SUGGESTIONS.crm;
  if (n.includes('hr') || n.includes('human')) return MODULE_SUGGESTIONS.hr;
  if (n.includes('analyt') || n.includes('report')) return MODULE_SUGGESTIONS.analytics;
  return MODULE_SUGGESTIONS.default;
}

export default function AdminModules() {
  const { api } = useAuth();
  const [panels,    setPanels]    = useState([]);
  const [permTypes, setPermTypes] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [panelState,setPanelState]= useState({});
  const [delTarget, setDelTarget] = useState(null);
  const [delLoad,   setDelLoad]   = useState(false);

  const initForm = () => ({ module_key: '', module_name: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pt] = await Promise.all([
        api.get('/admin/panels'),
        api.get('/admin/permission-types'),
      ]);
      setPanels(p.data);
      setPermTypes(pt.data);
      const state = {};
      p.data.forEach(panel => {
        state[panel.id] = { data: [], open: false, saving: false, form: initForm() };
      });
      setPanelState(state);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const togglePanel = async (panel) => {
    const pid = panel.id;
    const cur = panelState[pid];
    if (!cur.open && cur.data.length === 0) {
      try {
        const { data } = await api.get(`/admin/panels/${pid}/modules`);
        setPanelState(s => ({ ...s, [pid]: { ...s[pid], data, open: true } }));
      } catch { toast.error('Failed to load modules'); }
    } else {
      setPanelState(s => ({ ...s, [pid]: { ...s[pid], open: !cur.open } }));
    }
  };

  const setField = (pid, field, value) =>
    setPanelState(s => ({ ...s, [pid]: { ...s[pid], form: { ...s[pid].form, [field]: value } } }));

  // Fill form from a suggestion chip
  const applySuggestion = (pid, sugg, existingKeys) => {
    if (existingKeys.includes(sugg.key)) return toast.error(`"${sugg.name}" already exists`);
    setPanelState(s => ({
      ...s,
      [pid]: { ...s[pid], form: { module_key: sugg.key, module_name: sugg.name } },
    }));
  };

  const addModule = async (e, pid) => {
    e.preventDefault();
    const form = panelState[pid].form;
    setPanelState(s => ({ ...s, [pid]: { ...s[pid], saving: true } }));
    try {
      // auto sort_order = next position after existing modules
      const payload = { ...form, sort_order: panelState[pid].data.length + 1 };
      await api.post(`/admin/panels/${pid}/modules`, payload);
      toast.success(`Module "${form.module_name}" added`);
      const { data } = await api.get(`/admin/panels/${pid}/modules`);
      setPanelState(s => ({ ...s, [pid]: { ...s[pid], data, saving: false, form: initForm() } }));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error');
      setPanelState(s => ({ ...s, [pid]: { ...s[pid], saving: false } }));
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDelLoad(true);
    const { panelId, moduleId } = delTarget;
    try {
      await api.delete(`/admin/panels/${panelId}/modules/${moduleId}`);
      toast.success('Module removed');
      setPanelState(s => ({
        ...s,
        [panelId]: { ...s[panelId], data: s[panelId].data.filter(m => m.id !== moduleId) },
      }));
      setDelTarget(null);
    } catch (err) { toast.error(err?.response?.data?.message || 'Cannot delete — module may be in use by roles'); }
    finally { setDelLoad(false); }
  };

  if (loading) return (
    <div className="flex-center" style={{ minHeight: 300 }}>
      <span className="spinner" style={{ width: 32, height: 32 }}/>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Panel Modules</h1>
          <p className="page-subtitle">
            Add modules to your panels — they appear as rows in the permission matrix when creating roles and employees.
          </p>
        </div>
      </div>

      {/* Permission types banner */}
      {permTypes.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px',
          background: 'var(--bg-input)', borderRadius: 'var(--r-md)',
          border: '1px solid var(--border)', marginBottom: 20,
        }}>
          <KeyRound size={15} color="var(--brand)" style={{ marginTop: 3, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '.8rem', fontWeight: 600, marginBottom: 5 }}>
              Permission columns active in matrix
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {permTypes.map(pt => (
                <span key={pt.id} style={{
                  fontSize: '.72rem', padding: '2px 10px',
                  background: 'var(--brand)18', border: '1px solid var(--brand)44',
                  borderRadius: 20, color: 'var(--brand)', fontWeight: 600,
                }}>
                  {pt.label}
                </span>
              ))}
            </div>
            <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
              Contact Super Admin to add custom permission types (Export, Approve, etc.)
            </div>
          </div>
        </div>
      )}

      {panels.length === 0 ? (
        <div className="card">
          <div className="empty-state"><Layers size={40}/><h3>No panels assigned</h3><p>Contact Super Admin to assign panels to you.</p></div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {panels.map(panel => {
            const ps = panelState[panel.id] || { data: [], open: false, saving: false, form: initForm() };
            const suggestions = getSuggestions(panel.name);
            const existingKeys = ps.data.map(m => m.module_key);
            const unusedSuggestions = suggestions.filter(s => !existingKeys.includes(s.key));

            return (
              <div key={panel.id} className="card" style={{ overflow: 'hidden' }}>

                {/* ── Panel header row ── */}
                <div
                  onClick={() => togglePanel(panel)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 20px', cursor: 'pointer',
                    borderLeft: `4px solid ${panel.color}`,
                    background: ps.open ? panel.color + '08' : 'transparent',
                    borderBottom: ps.open ? '1px solid var(--border)' : 'none',
                    transition: 'background 150ms',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 'var(--r-md)',
                      background: panel.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontWeight: 800, color: panel.color, fontSize: '1rem' }}>{panel.name[0]}</span>
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '.95rem' }}>{panel.name}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>
                        {panel.code}
                        {ps.data.length > 0 && (
                          <span style={{ marginLeft: 8, color: panel.color, fontWeight: 600 }}>
                            {ps.data.length} module{ps.data.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {ps.open && ps.data.length === 0 && (
                          <span style={{ marginLeft: 8, color: 'var(--warning)' }}>no modules yet</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {ps.data.length > 0 && (
                      <span style={{
                        fontSize: '.72rem', padding: '3px 10px', borderRadius: 20, fontWeight: 600,
                        background: panel.color + '18', color: panel.color, border: `1px solid ${panel.color}44`,
                      }}>
                        <Package size={10} style={{ marginRight: 4 }}/>{ps.data.length}
                      </span>
                    )}
                    {ps.open
                      ? <ChevronDown size={16} color="var(--text-muted)"/>
                      : <ChevronRight size={16} color="var(--text-muted)"/>
                    }
                  </div>
                </div>

                {/* ── Expanded content ── */}
                {ps.open && (
                  <div style={{ padding: '20px' }}>

                    {/* Quick-add suggestion chips */}
                    {unusedSuggestions.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Zap size={13} color="var(--warning)"/> Quick Add — click to auto-fill
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {unusedSuggestions.map(s => (
                            <button
                              key={s.key}
                              type="button"
                              onClick={() => applySuggestion(panel.id, s, existingKeys)}
                              style={{
                                padding: '4px 12px', fontSize: '.75rem', fontWeight: 500,
                                border: '1px dashed var(--border)', borderRadius: 20,
                                background: 'transparent', cursor: 'pointer', color: 'var(--text-primary)',
                                transition: 'all 150ms',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = panel.color; e.currentTarget.style.color = panel.color; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                            >
                              + {s.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── Add module form ── */}
                    <div style={{
                      background: 'var(--bg-base)', borderRadius: 'var(--r-md)',
                      border: '1px solid var(--border)', padding: '16px', marginBottom: 20,
                    }}>
                      <div style={{ fontSize: '.82rem', fontWeight: 700, marginBottom: 14 }}>
                        Add New Module
                      </div>
                      <form onSubmit={e => addModule(e, panel.id)}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
                          {/* Module Key */}
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label" style={{ fontSize: '.75rem' }}>
                              Module Key <span className="req">*</span>
                            </label>
                            <input
                              className="form-input"
                              placeholder="e.g. invoices"
                              value={ps.form.module_key}
                              onChange={e => setField(panel.id, 'module_key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                              required
                            />
                            <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', marginTop: 3 }}>
                              unique key · lowercase · no spaces
                            </div>
                          </div>

                          {/* Module Name */}
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label" style={{ fontSize: '.75rem' }}>
                              Module Name <span className="req">*</span>
                            </label>
                            <input
                              className="form-input"
                              placeholder="e.g. Invoices"
                              value={ps.form.module_name}
                              onChange={e => setField(panel.id, 'module_name', e.target.value)}
                              required
                            />
                            <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', marginTop: 3 }}>
                              label shown in permission matrix
                            </div>
                          </div>

                          {/* Submit */}
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={ps.saving}
                            style={{ height: 38, whiteSpace: 'nowrap', alignSelf: 'flex-start', marginTop: 20 }}
                          >
                            {ps.saving ? <span className="spinner"/> : <><Plus size={14}/> Add</>}
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* ── Modules table with permission matrix preview ── */}
                    {ps.data.length === 0 ? (
                      <div style={{
                        padding: '28px 0', textAlign: 'center', color: 'var(--text-muted)',
                        border: '1px dashed var(--border)', borderRadius: 'var(--r-md)',
                      }}>
                        <Package size={28} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.4 }}/>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>No modules yet</div>
                        <div style={{ fontSize: '.78rem' }}>
                          Use the form above or click a Quick Add chip to get started.
                        </div>
                        <div style={{ fontSize: '.72rem', marginTop: 6, color: panel.color, fontWeight: 500 }}>
                          Example: key = <code style={{ background: 'var(--bg-input)', padding: '1px 5px', borderRadius: 3 }}>invoices</code>
                          &nbsp;· name = <code style={{ background: 'var(--bg-input)', padding: '1px 5px', borderRadius: 3 }}>Invoices</code>
                          &nbsp;· order = <code style={{ background: 'var(--bg-input)', padding: '1px 5px', borderRadius: 3 }}>1</code>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
                          Permission Matrix Preview — columns are the active permission types
                        </div>
                        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
                          <table style={{ margin: 0, fontSize: '.8rem' }}>
                            <thead>
                              <tr style={{ background: 'var(--bg-input)' }}>
                                <th style={{ width: 32, padding: '8px 10px' }}>#</th>
                                <th style={{ padding: '8px 10px' }}>Key</th>
                                <th style={{ padding: '8px 10px' }}>Module Name</th>
                                {permTypes.map(pt => (
                                  <th key={pt.id} style={{
                                    textAlign: 'center', padding: '8px 10px', minWidth: 54,
                                    color: 'var(--brand)', fontSize: '.72rem', fontWeight: 700,
                                  }}>
                                    {pt.label}
                                  </th>
                                ))}
                                <th style={{ width: 44 }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {ps.data.map((m, i) => (
                                <tr key={m.id}>
                                  <td style={{ padding: '8px 10px', color: 'var(--text-muted)', fontSize: '.75rem' }}>{i + 1}</td>
                                  <td style={{ padding: '8px 10px' }}>
                                    <code style={{
                                      background: 'var(--bg-input)', padding: '2px 8px',
                                      borderRadius: 4, fontSize: '.75rem', color: panel.color,
                                    }}>
                                      {m.module_key}
                                    </code>
                                  </td>
                                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>{m.module_name}</td>
                                  {permTypes.map(pt => (
                                    <td key={pt.id} style={{ padding: '8px 10px', textAlign: 'center' }}>
                                      <div style={{
                                        width: 18, height: 18, borderRadius: 4, margin: '0 auto',
                                        border: '1.5px solid var(--border)', background: 'var(--bg-input)',
                                        opacity: 0.6,
                                      }} title={`${pt.label} — set per role in Roles & Permissions`}/>
                                    </td>
                                  ))}
                                  <td style={{ padding: '8px 10px' }}>
                                    <button
                                      className="btn btn-ghost btn-sm btn-icon"
                                      style={{ color: 'var(--danger)' }}
                                      title="Remove module"
                                      onClick={() => setDelTarget({ panelId: panel.id, moduleId: m.id, moduleName: m.module_name })}
                                    >
                                      <Trash2 size={13}/>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div style={{
                            padding: '7px 14px', background: 'var(--bg-input)',
                            borderTop: '1px solid var(--border)',
                            fontSize: '.7rem', color: 'var(--text-muted)',
                          }}>
                            Checkbox values are assigned per role in <strong>Roles &amp; Permissions</strong> or per employee in <strong>Employees</strong>.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!delTarget}
        onClose={() => setDelTarget(null)}
        onConfirm={confirmDelete}
        loading={delLoad}
        title="Remove Module"
        message={`Remove "${delTarget?.moduleName}"? All role and employee permissions linked to this module will also be deleted.`}
      />
    </div>
  );
}
